import { createContext, useContext, useEffect, useState } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { get, ref } from "firebase/database";
import { database } from "../firebase/firebase";
import { buildTheme } from "../mui/theme";

// Initialize Firebase Auth
const auth = getAuth();

const AppContext = createContext();

export const AppProvider = ({ children }) => {
    const [theme, setThemeState] = useState("light");
    const [muiTheme, setMuiTheme] = useState(buildTheme());
    const [user, setUser] = useState(null);
    const [loadingUser, setLoadingUser] = useState(true);

    // --- Theme Handling ---

    // Apply theme globally (state + DOM + localStorage)
    const applyTheme = (newTheme) => {
        setThemeState(newTheme);
        document.documentElement.setAttribute("data-theme", newTheme);
        localStorage.setItem("theme", newTheme);
        setMuiTheme(buildTheme());
    };

    // How to use setTheme
    // setTheme("dark") → force dark
    // setTheme("light") → force light
    // setTheme() → toggle
    const setTheme = (newTheme) => {
        if (!newTheme) {
            const toggled = theme === "dark" ? "light" : "dark";
            applyTheme(toggled);
        } else {
            applyTheme(newTheme);
        }
    };

    // Load initial theme from localStorage or system preference
    useEffect(() => {
        const savedTheme = localStorage.getItem("theme");
        const systemPrefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
        const initialTheme = savedTheme || (systemPrefersDark ? "dark" : "light");
        applyTheme(initialTheme);
    }, []);

    // ### Auth Handling ###
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
            if (authUser) {
                const userRef = ref(database, `users/${authUser.uid}`);
                const snapshot = await get(userRef);

                if (snapshot.exists()) {
                    setUser({
                        uid: authUser.uid,
                        email: authUser.email,
                        displayName: snapshot.val().user_name,
                        role: snapshot.val().user_role,
                    });
                } else {
                    // User exists in auth but not in Realtime DB
                    setUser({
                        uid: authUser.uid,
                        email: authUser.email,
                        displayName: "Customer User",
                        role: "Customer",
                    });
                }
            } else {
                setUser(null); // Not logged in
            }
            setLoadingUser(false);
        });

        return () => unsubscribe();
    }, []);

    return (
        <AppContext.Provider value={{ theme, setTheme, muiTheme, user, setUser, loadingUser }}>
            {children}
        </AppContext.Provider>
    );
};

export const useAppContext = () => {
    const context = useContext(AppContext);
    if (!context) throw new Error("useAppContext must be used within AppProvider");
    return context;
};
